import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FullLogo } from '@/components/brand/Marks';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle, KeyRound, Loader2, ShieldCheck } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', slate: '#5C6B7A' };

const INVALID_COPY = {
  not_found: 'This link is not valid. Check the link in your email, or ask for a new invite.',
  expired: 'This link has expired — they last 72 hours. Ask for a new one; it costs nothing.',
  accepted: 'This link has already been used. Sign in with the password you set.',
};

const Shell = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center px-4" style={{ background: '#F5F7FA' }} data-testid="activate-page">
    <div className="w-full max-w-md">
      <div className="mb-8 flex justify-center"><FullLogo size="md" /></div>
      <div className="bg-white p-8" style={{ border: '1px solid rgba(136,150,168,0.25)' }}>{children}</div>
      <p className="text-center text-[11px] mt-4" style={{ color: C.slate }}>frAIme by underAIter.com — AI-native underwriting</p>
    </div>
  </div>
);

export default function ActivateLoginPage() {
  const { token } = useParams();
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    axios.get(`${API}/login-invite/${token}`)
      .then(r => setInvite(r.data))
      .catch(() => setInvite({ valid: false, reason: 'not_found' }));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    const min = invite?.min_password || 12;
    if (password.length < min) { setError(`Choose at least ${min} characters.`); return; }
    if (password !== confirm) { setError('Those two passwords do not match.'); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/login-invite/${token}/accept`, { password });
      setDone(data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'That did not work — please try again.');
      setBusy(false);
    }
  };

  if (!invite) return <Shell><div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin" style={{ color: C.blue }} /></div></Shell>;

  if (done) return (
    <Shell>
      <div className="text-center py-3" data-testid="activate-done">
        <CheckCircle className="w-10 h-10 mx-auto mb-3" style={{ color: C.green }} />
        <h1 className="text-lg font-semibold mb-1" style={{ color: C.navy }}>Your password is set</h1>
        <p className="text-sm mb-1" style={{ color: C.slate }}>
          It is known only to you — nobody on the platform can read it back.
        </p>
        {done.mfa_required && (
          <p className="text-[12px] mb-4 p-3 text-left" style={{ background: 'rgba(0,180,216,0.06)', color: C.navy }}>
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1" style={{ color: C.teal }} />
            One more step on your first sign-in: multi-factor authentication is enforced for your role, so
            have an authenticator app ready (Google Authenticator, 1Password, Authy — any of them).
          </p>
        )}
        <a href="/login?staff=1" data-testid="activate-signin-link"
          className="inline-block mt-2 px-5 py-2.5 text-sm font-semibold text-white" style={{ background: C.blue }}>
          Sign in now →
        </a>
      </div>
    </Shell>
  );

  if (!invite.valid) return (
    <Shell>
      <div className="text-center py-4" data-testid="activate-invalid">
        <AlertCircle className="w-10 h-10 mx-auto mb-3" style={{ color: C.red }} />
        <p className="text-sm" style={{ color: C.navy }}>{INVALID_COPY[invite.reason] || INVALID_COPY.not_found}</p>
        <a href="/login?staff=1" className="inline-block mt-5 text-sm font-semibold hover:underline" style={{ color: C.blue }}>Go to sign in →</a>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <p className="text-[10px] uppercase tracking-[0.15em] mb-1" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Account activation</p>
      <h1 className="text-xl font-semibold mb-1" style={{ color: C.navy }} data-testid="activate-heading">
        Set your password, {invite.name}
      </h1>
      <p className="text-sm mb-5" style={{ color: C.slate }}>
        You're joining frAIme as <b style={{ color: C.navy }} data-testid="activate-role">{invite.role_label}</b> on <b>{invite.email_masked}</b>.
        We never send passwords by email — this link works once, so choose yours now.
      </p>

      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wider" style={{ color: C.slate }}>New password</Label>
          <Input type="password" value={password} onChange={e => setPassword(e.target.value)} autoFocus
            placeholder={`At least ${invite.min_password || 12} characters`} className="mt-1 rounded-sm"
            required data-testid="activate-password-input" />
          <p className="text-[11px] mt-1" style={{ color: C.slate }}>
            {invite.min_password || 12}+ characters, mixing letters with a number or symbol. This account can bind
            risk and move money, so a passphrase beats a password.
          </p>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider" style={{ color: C.slate }}>Confirm password</Label>
          <Input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
            className="mt-1 rounded-sm" required data-testid="activate-confirm-input" />
        </div>
        <Button type="submit" disabled={busy} className="w-full rounded-sm font-semibold text-white"
          style={{ background: C.green, height: 44 }} data-testid="activate-submit-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
          {busy ? 'Setting your password…' : 'Set password'}
        </Button>
      </form>

      {error && (
        <div className="flex items-center gap-2 text-sm p-3 mt-4" style={{ background: 'rgba(226,75,74,0.1)', color: C.red }} data-testid="activate-error">
          <AlertCircle className="w-4 h-4 shrink-0" /><span>{error}</span>
        </div>
      )}
    </Shell>
  );
}
